import { DateFormat, formatDateTimeWithLocale } from '@frontend/common';
import { Application } from '@frontend/functionality/types';
import { getApplicationDisplayName, getApplicationIcon } from '@frontend/functionality/utils';
import { useLocale } from '@frontend/lang';
import { useAccountRepository } from '@frontend/repository';
import { FaChevronRight } from 'react-icons/fa6';
import { FormattedMessage } from 'react-intl';

import { useMenuRepository } from '../../menu/menu-repository';

interface ApplicationCardProps {
    application: Application | 'my';
}

const ApplicationCard = (props: ApplicationCardProps) => {
    const { locale } = useLocale();
    const {
        currentAccount,
        store: { availableAccountsInfo }
    } = useAccountRepository();
    const { currentApp, previouslyUsedApps, setCurrentApp } = useMenuRepository();
    const foundApp = previouslyUsedApps.find((app) => app.appType === props.application);
    const Icon = getApplicationIcon(props.application);

    let accountName = '';
    if (props.application === 'my' && availableAccountsInfo.find((a) => a.id === currentAccount)) {
        accountName = ' ' + availableAccountsInfo.find((a) => a.id === currentAccount)!.name;
    }

    return (
        <div
            onClick={(e) => {
                e.stopPropagation();
                setCurrentApp(props.application);
            }}
            className='bg-background-surface inline-flex h-16 w-full items-center justify-between self-stretch rounded-2xl p-3'>
            <div className='flex items-center justify-start gap-2'>
                <div className='bg-brand-primary flex size-12 items-center justify-center gap-3 rounded-xl'>
                    <Icon className='text-background-surface size-10 p-1' />
                </div>
                <div className='inline-flex h-12 flex-col items-start justify-center gap-0.5'>
                    <div className='text-foreground-default justify-start text-base font-semibold'>
                        {props.application && getApplicationDisplayName(props.application)} {accountName}
                    </div>
                    <div className='text-foreground-default justify-start text-sm font-medium'>
                        {currentApp === props.application ? (
                            <FormattedMessage
                                id='applicationCard.currentlyOpen'
                                defaultMessage='Currently open'
                                description='Message displayed on the app that is currently open'
                            />
                        ) : foundApp ? (
                            <FormattedMessage
                                id='applicationCard.last-used'
                                defaultMessage='Last used • {value}'
                                description='Message displayed about the last app use'
                                values={{
                                    value: formatDateTimeWithLocale(new Date(foundApp.lastUse), locale, DateFormat.DD_MMM)
                                }}
                            />
                        ) : (
                            <FormattedMessage
                                id='applicationCard.neverUsed'
                                defaultMessage='Never used'
                                description='Message displayed when the app has never been used'
                            />
                        )}
                    </div>
                </div>
            </div>
            <div className={`flex size-6 items-center justify-center gap-2.5 ${currentApp === props.application ? 'hidden' : 'text-foreground-default'}`}>
                <FaChevronRight />
            </div>
        </div>
    );
};

export default ApplicationCard;
